import { useState } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputLabel, TextField, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import { Plus, X } from "lucide-react";
import type { DetailFieldKind, OnboardingAnswers, OnboardingDetailItem } from "../../types/onboarding";
import { emailMessage } from "../../utils/storefrontFields";
import { PLAIN_BUTTONS } from "./buttons";
import DateField from "./DateField";

type Details = Record<string, unknown>;
type TableRow = Record<string, string>;

interface Field {
  key: string;
  label: string;
  kind: DetailFieldKind;
}

interface Column {
  key: string;
  label: string;
  options?: Array<{ value: string; label: string }>;
}

interface Props {
  item: OnboardingDetailItem;
  answers: OnboardingAnswers;
  items: OnboardingDetailItem[];
  saving: boolean;
  onSave: (details: Details) => Promise<string | null>;
}

// The API says which fields a step records (DETAIL_FIELDS in onboardingSchemas); these are their labels.
const FIELD_LABELS: Record<string, string> = {
  billingEmail: "Billing email",
  billingAddress: "Billing address",
  taxExempt: "Tax-exempt",
  taxId: "Tax ID (optional)",
  ethocaCaseId: "Ethoca case ID",
  verifiCaseId: "Verifi case ID",
  rdrCaseId: "RDR case ID",
  cutoverDate: "Cutover date",
  verifiClientId: "Verifi Client ID",
  productWebsite: "Product website",
  supportContactName: "Support contact name",
  supportContactEmail: "Support contact email",
  effectiveDate: "Effective date",
};

const YES_NO = [{ value: true, label: "Yes" }, { value: false, label: "No" }];

const STORE_COLUMNS: Column[] = [
  { key: "storeName", label: "Store name" },
  { key: "myshopifyUrl", label: "MyShopify URL" },
  { key: "collaboratorCode", label: "Collaborator code" },
  { key: "paymentDescriptor", label: "Payment descriptor" },
];

const DESCRIPTOR_COLUMNS: Column[] = [{ key: "descriptor", label: "Descriptor" }, { key: "mid", label: "MID it runs on" }];

const CDRN_FIELDS: Field[] = [
  { key: "phone", label: "Phone", kind: "text" },
  { key: "email", label: "Email", kind: "email" },
  { key: "website", label: "Website", kind: "text" },
  { key: "disputesPerMonth", label: "Disputes a month", kind: "text" },
];

const toFields = (item: OnboardingDetailItem): Field[] | undefined => item.detailFields?.map((f) => ({
  ...f,
  label: f.key === "reference" ? (item.network === "verifi" ? "Verifi SR#" : "Ethoca thread") : FIELD_LABELS[f.key],
}));

function toMidColumns(answers: OnboardingAnswers): Column[] {
  const provider = answers.switching.provider;
  const scopes = answers.switching.enabled
    ? [
      { value: "moves", label: `Moves from ${provider}` },
      { value: "new", label: "New with us" },
      { value: "stays", label: `Stays with ${provider}` },
      { value: "dropped", label: "Dropped" },
    ]
    : [{ value: "covered", label: "We cover it at go-live" }, { value: "out_of_scope", label: "Out of scope" }];
  return [
    { key: "processor", label: "Processor" },
    { key: "mid", label: "MID" },
    { key: "merchantName", label: "Merchant name (DBA)" },
    { key: "acquirer", label: "Acquirer" },
    { key: "mcc", label: "MCC" },
    { key: "bin", label: "BIN" },
    { key: "caid", label: "CAID" },
    { key: "scope", label: "Scope", options: scopes },
  ];
}

// A choice that no longer applies, such as a MID scope after switching was turned off, starts unpicked.
const toTableRows = (value: unknown, columns: Column[]): TableRow[] =>
  (Array.isArray(value) ? value as TableRow[] : []).map((row) => Object.fromEntries(columns.map((c) => {
    const cell = row[c.key] ?? "";
    return [c.key, c.options && !c.options.some((o) => o.value === cell) ? "" : cell];
  })));

const emptyRow = (columns: Column[]): TableRow => Object.fromEntries(columns.map((c) => [c.key, ""]));

const withoutBlankRows = (rows: TableRow[]) => rows.filter((row) => Object.values(row).some((v) => v.trim()));

// A scope left unpicked is not saved: the API only takes the listed scopes.
const withoutEmptyScope = (rows: TableRow[]) => rows.map(({ scope, ...row }) => (scope ? { ...row, scope } : row));

// One row per Shopify store the checklist counts: it has one app install step per store's brand, unless an
// edit took that brand's store out.
const toStorePrefill = (items: OnboardingDetailItem[]): TableRow[] =>
  items.flatMap((i) => (i.itemKey === "shopify_app_install" && i.state !== "not_needed" && i.brandName
    ? [{ ...emptyRow(STORE_COLUMNS), storeName: i.brandName }]
    : []));

const toStoreDescriptors = (items: OnboardingDetailItem[]): TableRow[] =>
  toTableRows(items.find((i) => i.itemKey === "shopify_store_details")?.details.stores, STORE_COLUMNS)
    .filter((store) => store.paymentDescriptor)
    .map((store) => ({ descriptor: store.paymentDescriptor, mid: "" }));

const toCount = (value: unknown) => (Array.isArray(value) ? value.length : 0);

export default function ItemDetails({ item, answers, items, saving, onSave }: Props) {
  const fields = toFields(item);
  if (fields) return <FieldsForm fields={fields} details={item.details} saving={saving} onSave={onSave} />;
  if (item.itemKey === "mids") {
    const rdr = answers.alerts.enabled;
    return (
      <DetailsSummary
        rows={[
          { label: "MIDs", count: toCount(item.details.mids) },
          { label: "Descriptors", count: toCount(item.details.descriptors) },
          ...(rdr ? [{ label: "Refunded Visa ARNs", count: toCount(item.details.arns) }] : []),
        ]}
        dialog={(onClose) => <MidsDialog item={item} answers={answers} items={items} rdr={rdr} saving={saving} onSave={onSave} onClose={onClose} />}
      />
    );
  }
  if (item.itemKey === "shopify_store_details") {
    return (
      <DetailsSummary
        rows={[{ label: "Stores", count: toCount(item.details.stores) }]}
        dialog={(onClose) => <StoresDialog item={item} items={items} saving={saving} onSave={onSave} onClose={onClose} />}
      />
    );
  }
  return null;
}

// Follows the saved details while nothing has been changed since; edits typed during a save stay put.
function FieldsForm({ fields, details, saving, onSave }: { fields: Field[]; details: Details; saving: boolean; onSave: Props["onSave"] }) {
  const toValues = (saved: Details): Details => Object.fromEntries(fields.map((f) => [f.key, saved[f.key] ?? (f.kind === "yesno" ? null : "")]));
  const [values, setValues] = useState<Details>(() => toValues(details));
  const [synced, setSynced] = useState(details);
  if (JSON.stringify(details) !== JSON.stringify(synced)) {
    setSynced(details);
    if (JSON.stringify(values) === JSON.stringify(toValues(synced))) setValues(toValues(details));
  }
  const [blurred, setBlurred] = useState<Set<string>>(new Set());
  const errors = Object.fromEntries(fields.map((f) => [f.key, f.kind === "email" ? emailMessage(String(values[f.key] ?? "")) : null]));
  const set = (key: string, value: unknown) => setValues((prev) => ({ ...prev, [key]: value }));
  const blur = (key: string) => setBlurred((prev) => new Set(prev).add(key));

  return (
    <div role="group" aria-label="Details" className="flex flex-col gap-3">
      <span className="text-xs font-medium text-gray-500">Details</span>
      <FieldInputs fields={fields} values={values} errors={errors} blurred={blurred} onChange={set} onBlur={blur} />
      <div className="flex justify-end">
        <Button
          size="small" variant="outlined" disabled={saving || Object.values(errors).some(Boolean)}
          onClick={() => onSave(Object.fromEntries(Object.entries(values).filter(([, v]) => v !== null)))}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function FieldInputs({ fields, values, errors, blurred, onChange, onBlur }: {
  fields: Field[];
  values: Details;
  errors: Record<string, string | null>;
  blurred: Set<string>;
  onChange: (key: string, value: unknown) => void;
  onBlur: (key: string) => void;
}) {
  return (
    <>
      {fields.map((f) => (f.kind === "yesno"
        ? (
          <div key={f.key} className="flex flex-col">
            <InputLabel>{f.label}</InputLabel>
            <ToggleButtonGroup
              exclusive size="small" aria-label={f.label} value={values[f.key]} sx={{ width: 160 }}
              onChange={(_, value: boolean | null) => value !== null && onChange(f.key, value)}
            >
              {YES_NO.map((o) => <ToggleButton key={o.label} value={o.value} sx={{ flex: 1 }}>{o.label}</ToggleButton>)}
            </ToggleButtonGroup>
          </div>
        )
        : f.kind === "date" ? (
          <DateField
            key={f.key} label={f.label} value={String(values[f.key] ?? "")}
            error={blurred.has(f.key) && !!errors[f.key]} helperText={blurred.has(f.key) ? errors[f.key] : null}
            onChange={(value) => onChange(f.key, value)} onBlur={() => onBlur(f.key)}
          />
        )
        : (
          <TextField
            key={f.key} label={f.label} value={String(values[f.key] ?? "")} size="small" fullWidth
            multiline={f.kind === "multiline"} minRows={f.kind === "multiline" ? 2 : undefined}
            error={blurred.has(f.key) && !!errors[f.key]} helperText={blurred.has(f.key) ? errors[f.key] : null}
            onChange={(e) => onChange(f.key, e.target.value)} onBlur={() => onBlur(f.key)}
          />
        )))}
    </>
  );
}

// The dialogs mount only while open, so Cancel drops unsaved edits and every opening starts from what is saved.
function DetailsSummary({ rows, dialog }: { rows: Array<{ label: string; count: number }>; dialog: (onClose: () => void) => React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-gray-500">Details</span>
      <div className="rounded-lg border border-gray-100 text-sm dark:border-gray-800">
        {rows.map((r) => (
          <div key={r.label} className="flex justify-between border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-gray-800">
            <span className="text-gray-700 dark:text-gray-300">{r.label}</span>
            <span className="font-medium text-gray-800 dark:text-white/90">{r.count}</span>
          </div>
        ))}
      </div>
      <Button size="small" variant="outlined" sx={{ alignSelf: "flex-end" }} onClick={() => setOpen(true)}>Edit</Button>
      {open && dialog(() => setOpen(false))}
    </div>
  );
}

function MidsDialog({ item, answers, items, rdr, saving, onSave, onClose }: Props & { rdr: boolean; onClose: () => void }) {
  const midColumns = toMidColumns(answers);
  // The CDRN contact is for new enrolments, not a transfer from the previous provider.
  const cdrn = answers.alerts.enabled && (!answers.switching.enabled || answers.switching.someMidsNew);
  const [mids, setMids] = useState(() => toTableRows(item.details.mids, midColumns));
  const [descriptors, setDescriptors] = useState(() => ("descriptors" in item.details ? toTableRows(item.details.descriptors, DESCRIPTOR_COLUMNS) : toStoreDescriptors(items)));
  const [arns, setArns] = useState(() => (Array.isArray(item.details.arns) ? item.details.arns.join("\n") : ""));
  const [contact, setContact] = useState<Details>(() => ({ ...emptyRow(CDRN_FIELDS), ...(item.details.cdrnContact as Details | undefined) }));
  const [error, setError] = useState<string | null>(null);
  const contactErrors = { email: emailMessage(String(contact.email ?? "")) };

  const save = async () => {
    const filledDescriptors = withoutBlankRows(descriptors);
    const failed = await onSave({
      mids: withoutEmptyScope(withoutBlankRows(mids)),
      // An empty list saved before the store details exist would stop the store descriptors being offered later.
      ...(filledDescriptors.length || "descriptors" in item.details ? { descriptors: filledDescriptors } : {}),
      arns: arns.split("\n").map((a) => a.trim()).filter(Boolean),
      // Details are replaced whole, so a CDRN contact saved before the block was hidden stays.
      ...(cdrn ? { cdrnContact: contact } : item.details.cdrnContact ? { cdrnContact: item.details.cdrnContact } : {}),
    });
    if (failed) setError(failed); else onClose();
  };

  return (
    <Dialog open fullWidth maxWidth="lg" sx={PLAIN_BUTTONS} onClose={(_, reason) => { if (reason !== "backdropClick") onClose(); }}>
      <DialogTitle>MIDs and descriptors</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-5 pt-1">
          {error && <Alert severity="error">{error}</Alert>}
          <RowsEditor title="MIDs" addLabel="Add MID" columns={midColumns} rows={mids} onChange={setMids} />
          <RowsEditor title="Descriptors" addLabel="Add descriptor" columns={DESCRIPTOR_COLUMNS} rows={descriptors} onChange={setDescriptors} />
          {rdr && (
            <TextField
              label="Refunded Visa ARNs, one per line" multiline minRows={3} size="small" fullWidth value={arns}
              onChange={(e) => setArns(e.target.value)}
            />
          )}
          {cdrn && (
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">CDRN contact</span>
              <div className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
                <FieldInputs
                  fields={CDRN_FIELDS} values={contact} errors={contactErrors} blurred={new Set(["email"])}
                  onChange={(key, value) => setContact((prev) => ({ ...prev, [key]: value }))} onBlur={() => undefined}
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !!contactErrors.email} onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function StoresDialog({ item, items, saving, onSave, onClose }: Omit<Props, "answers"> & { onClose: () => void }) {
  const [stores, setStores] = useState(() => ("stores" in item.details ? toTableRows(item.details.stores, STORE_COLUMNS) : toStorePrefill(items)));
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const failed = await onSave({ stores: withoutBlankRows(stores) });
    if (failed) setError(failed); else onClose();
  };

  return (
    <Dialog open fullWidth maxWidth="md" sx={PLAIN_BUTTONS} onClose={(_, reason) => { if (reason !== "backdropClick") onClose(); }}>
      <DialogTitle>Shopify stores</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4 pt-1">
          {error && <Alert severity="error">{error}</Alert>}
          <RowsEditor title="Stores" addLabel="Add store" columns={STORE_COLUMNS} rows={stores} onChange={setStores} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function RowsEditor({ title, addLabel, columns, rows, onChange }: {
  title: string;
  addLabel: string;
  columns: Column[];
  rows: TableRow[];
  onChange: (rows: TableRow[]) => void;
}) {
  const setCell = (index: number, key: string, value: string) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)));

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</span>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-gray-500 dark:bg-white/5">
              {columns.map((c) => <th key={c.key} className="px-2 py-2 font-medium">{c.label}</th>)}
              <th className="w-10 px-2 py-2"><span className="sr-only">Remove</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                {columns.map((c) => (
                  <td key={c.key} className="min-w-[110px] px-2 py-1.5">
                    <TextField
                      select={!!c.options} value={row[c.key]} size="small" fullWidth onChange={(e) => setCell(i, c.key, e.target.value)}
                      slotProps={{ htmlInput: { "aria-label": `${c.label}, row ${i + 1}` }, select: { native: true } }}
                    >
                      {c.options && <option value="">Pick</option>}
                      {c.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </TextField>
                  </td>
                ))}
                <td className="px-2 py-1.5">
                  <IconButton size="small" aria-label={`Remove row ${i + 1}`} onClick={() => onChange(rows.filter((_, j) => j !== i))}><X size={14} /></IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button size="small" startIcon={<Plus size={14} />} sx={{ alignSelf: "flex-start" }} onClick={() => onChange([...rows, emptyRow(columns)])}>{addLabel}</Button>
    </div>
  );
}
